/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import { yupResolver } from '@hookform/resolvers/yup';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LocalStorageService } from 'api';
import { Copyright, Message, ProgressButton } from 'components';
import { path } from 'ramda';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLazyGetUserQuery, useLoginMutation } from 'store/api/version-api.slice';
import { showMessage } from 'store/slices/message.slice';

import LOGIN_SCHEMA from './login.shema';
import { styles } from './styles';

const DEFAULT_VALUES = {
	mc_email_address: '',
	mc_password: ''
};

const localStorageService = new LocalStorageService();

const LoginRoute = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const [getUser, { isLoading: isUserLoading }] = useLazyGetUserQuery();
	const [login, { isLoading: isLoginLoading }] = useLoginMutation();

	const {
		handleSubmit,
		control,
		formState: { errors }
	} = useForm({
		defaultValues: DEFAULT_VALUES,
		resolver: yupResolver(LOGIN_SCHEMA)
	});

	const dispatch = useDispatch();

	const signIn = async data => {
		const { mc_email_address, mc_password } = data;

		const response = await login({
			username: mc_email_address,
			password: mc_password
		});

		if (response.error) {
			dispatch(
				showMessage({
					message: response.error.data.detail,
					variant: 'error'
				})
			);
			return;
		}

		localStorageService.setTokens(response.data.access, response.data.refresh);

		const currentUser = await getUser();

		const {
			data: { next_url }
		} = currentUser;

		if (next_url === 0) {
			if (location.pathname.startsWith('/renew_member/')) {
				navigate(location.pathname);
			} else {
				navigate('/dashboard');
			}
		} else {
			navigate('/onboarding');
		}
		window._hsq = window._hsq || [];

		const { _hsq } = window;
		_hsq.push(['identify', { email: mc_email_address }]);
		_hsq.push(['trackPageView']);
	};

	useEffect(() => {
		document.title = 'Member Center - Login';

		window._hsq = window._hsq || [];

		const { _hsq } = window;

		_hsq.push(['setPath', '/login']);
		_hsq.push(['trackPageView']);
	}, []);

	return (
		<Container component="main" maxWidth="xs">
			<Box sx={styles.paper}>
				<Avatar sx={styles.avatar}>
					<LockOutlinedIcon />
				</Avatar>
				<Typography component="h1" variant="h5">
					Member Center
				</Typography>
				<form id="Login_Form" className="login_form" onSubmit={handleSubmit(signIn)}>
					<label style={{ display: 'none' }}>
						HubspotCollectedFormsWorkaround
						https://community.hubspot.com/t5/APIs-Integrations/How-to-stop-collected-forms-from-collecting-a-form/m-p/299172#M28102
					</label>
					<input name="credit-card" style={{ display: 'none' }} required="" id="credit-card"></input>
					<Controller
						name="mc_email_address"
						control={control}
						render={({ field }) => {
							return (
								<TextField
									{...field}
									error={Boolean(errors?.mc_email_address)}
									variant="outlined"
									margin="normal"
									fullWidth
									id="email"
									label="Email Address"
									helperText={path(['mc_email_address', 'message'])(errors)}
									autoComplete="mc_email_address"
									autoFocus
								/>
							);
						}}
					/>

					<Controller
						name="mc_password"
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								error={Boolean(errors?.mc_password)}
								variant="outlined"
								margin="normal"
								fullWidth
								label="Password"
								type="password"
								id="password"
								autoComplete="current-password"
								helperText={path(['mc_password', 'message'])(errors)}
							/>
						)}
					/>

					<Message />
					<ProgressButton
						circleColor="secondary"
						isLoading={isLoginLoading || isUserLoading}
						button={
							<Button fullWidth variant="contained" color="primary" type="submit" sx={styles.submit}>
								Sign In
							</Button>
						}
					/>
					<Grid container>
						<Grid item>
							<Link to="/signup" variant="body2">
								{"Don't have an account? Sign Up"}
							</Link>
						</Grid>
					</Grid>
				</form>
			</Box>
			<Box mt={8}>
				<Copyright name="Member Center" />
			</Box>
		</Container>
	);
};

export default LoginRoute;
